import { FormikProps, getIn } from 'formik'
import { nanoid } from 'nanoid'
import * as yup from 'yup'

import {
  EActionStepType,
  TActionApiCallStep,
  TActionStep,
} from '~/features/scenarios/components/createScenarioFlow/createCustomScenarioFlow/customScenarioStepThree/types'
import { DEFAULT_SCENARIO_NAME } from '~/features/scenarios/constants'
import {
  EFormParameterListKeys,
  EParameterListKeys,
  EScenarioActionKeys,
  EScenarioActionMethod,
  EScenarioActionUsesTemplate,
  EScenarioAgentKeys,
  EScenarioKeys,
  TAgentCustomField,
  TFormParameterListContent,
  TFormScenarioActionPayload,
  TInstructionStep,
  TParameterListContent,
  TScenario,
  TScenarioAction,
  TScenarioActionPayload,
  TScenarioAgent,
  TScenarioAgentCustomFields,
  TScenarioPayload,
  TUpdateScenarioPayload,
  TUpdateScenarioTitlePayload,
  EFormAgentCustomFieldKeys,
  TScenarioActionProps,
  TScenarioNewAction,
  TFormHeaderListContent,
  EActionKeys,
  TAction,
  TParameterListType,
} from '~/features/scenarios/models'
import { EClientPath, EServerPath } from '~/models/api'
import pathBuilder from '~/utils/api'

export const queryKeys = {
  scenarios: (orgId: string) => [EServerPath.scenarios, orgId],
  scenario: (id: string) => [EServerPath.scenario, id],
  scenarioAgents: (scenarioId: string) => [
    EServerPath.scenarioAgents,
    scenarioId,
  ],
  scenarioAgentsForAdmin: (scenarioId: string) => [
    EServerPath.scenarioAgentsForAdmin,
    scenarioId,
  ],
  scenarioAgent: (scenarioId: string, agentId: string) => [
    EServerPath.scenarioAgent,
    scenarioId,
    agentId,
  ],
  scenarioActions: () => [EServerPath.actions],
  scenarioAction: (actionId: string) => [EServerPath.action, actionId],
  actions: (orgId: string) => [EServerPath.actions, orgId],
  action: (id: string) => [EServerPath.action, id],
  scenarioTemplates: () => [EServerPath.scenarioTemplates],
  scenarioTemplate: (templateId: string) => [
    EServerPath.scenarioTemplates,
    templateId,
  ],
  integrationsCatalog: () => [EServerPath.integrationsCatalog],
  integrationsConnections: (orgId: string) => [
    EServerPath.integrationsConnections,
    orgId,
  ],
}

export const scenarioTitleValidationSchema = yup.object({
  [EScenarioKeys.name]: yup.string().required('Scenario name is required'),
})

export const scenarioValidationSchema = yup.object({
  [EScenarioKeys.name]: yup
    .string()
    .min(3, 'Name must be at least 3 characters long'),
  [EScenarioKeys.instructionSteps]: yup.string().optional(),
  [EScenarioKeys.actionIds]: yup.array().optional(),
  [EScenarioKeys.additionalInstructions]: yup.string().optional(),
  [EScenarioKeys.channels]: yup.array().optional(),
  [EScenarioKeys.triggerInstructions]: yup.string().optional(),
  [EScenarioKeys.agentCustomFields]: yup.array().of(
    yup.object({
      [EFormAgentCustomFieldKeys.key]: yup.string().required('Key is required'),
      [EFormAgentCustomFieldKeys.type]: yup
        .string()
        .required('Type is required'),
      [EFormAgentCustomFieldKeys.description]: yup.string().optional(),
      [EFormAgentCustomFieldKeys.required]: yup
        .boolean()
        .required('Some value is required'),
    }),
  ),
})

export const pathForScenario = (scenarioId: string) => {
  return pathBuilder(EClientPath.scenarioCreateEdit, { scenarioId })
}

export const pathForScenarioV2 = (scenarioId: string) => {
  return pathBuilder(EClientPath.scenarioCreateV2Edit, { scenarioId })
}

export const isDisabledAgent = (
  scenario: TScenario | null | undefined,
  agent: TScenarioAgent,
) =>
  Object.entries(scenario?.[EScenarioKeys.agentCustomFields] || []).some(
    ([key, value]) => value.required && !agent.customFields?.[key],
  )

export const getAssistantCheckboxTooltipTitle = (
  scenario: TScenario | null | undefined,
  agent: TScenarioAgent,
) =>
  isDisabledAgent(scenario, agent)
    ? 'Required details must be populated to activate scenarios for each assistant.'
    : null

export const getScenarioAgentValidationSchema = (
  allCustomFields: Record<string, TAgentCustomField> | undefined,
) =>
  yup.object({
    [EScenarioAgentKeys.active]: yup.boolean().required(),
    [EScenarioAgentKeys.customFields]: yup
      .object()
      .shape(
        Object.entries(allCustomFields || []).reduce(
          (acc, [key, value]) => {
            let fieldSchema: yup.Schema

            switch (typeof value.type) {
              case 'string':
                fieldSchema = yup.string()
                break
              case 'boolean':
                fieldSchema = yup.boolean()
                break
              case 'number':
                fieldSchema = yup.number()
                break
              default:
                throw new Error(`Unsupported field type: ${typeof value.type}`)
            }

            acc[key] = fieldSchema.nullable()
            return acc
          },
          {} as Record<string, yup.Schema<TScenarioAgentCustomFields>>,
        ),
      )
      .nullable(),
    [EScenarioAgentKeys.timezone]: yup.string().nullable(),
  })

export const pathForScenarioAction = (actionId: string) => {
  return pathBuilder(EClientPath.scenarioActionCreateEdit, {
    actionId,
  })
}

const actionNameBaseValidationSchema = yup
  .string()
  .matches(/^[a-zA-Z0-9_]+$/, {
    message: 'Only letters, numbers, and underscore are allowed',
  })
  .min(3, 'Name must be at least 3 characters long')
  .required('Action name is required')

export const scenarioActionTitleValidationSchema = yup.object({
  [EScenarioActionKeys.name]: actionNameBaseValidationSchema,
})

/**
 * Parameter names are used in URL path placeholders and as request parameter keys.
 * Allow characters common across APIs: path params (user-id, user_id), nested/array
 * query params (filter[status], user[name], ids[]), and dot-notation (user.name).
 */
const actionParameterNameValidationSchema = yup
  .string()
  .matches(
    /^[a-zA-Z0-9_.\-[\]]+$/,
    'Name can only contain letters, numbers, underscores, hyphens, periods, and brackets',
  )
  .max(60, 'Name is too long')
  .required('Name is required')

const getParameterListSchema = ({
  getParametersList,
}: {
  getParametersList: (
    context: yup.TestContext<yup.AnyObject>,
  ) => TFormParameterListContent[]
}) => ({
  [EFormParameterListKeys.name]: actionParameterNameValidationSchema.test(
    'unique-params',
    'Name must be unique',
    (name, context) => {
      if (!name) return true

      const parametersList = getParametersList(context)

      const names = parametersList.map(
        (param) => param[EFormParameterListKeys.name],
      )

      const nameCounts = names.reduce<Record<string, number>>(
        (acc, paramName) => {
          acc[paramName] = (acc[paramName] || 0) + 1
          return acc
        },
        {},
      )

      const duplicateNames = Object.entries(nameCounts)
        .filter(([, count]) => count > 1)
        .map(([paramName]) => paramName)

      const isValid = !duplicateNames.includes(name)

      return isValid
    },
  ),
  [EFormParameterListKeys.description]: yup
    .string()
    .max(191, 'Description is too long')
    .optional(),
  [EFormParameterListKeys.type]: yup
    .mixed()
    .oneOf(['string', 'number', 'boolean', 'object', 'array'], 'Invalid type')
    .required('Type is required'),
  [EFormParameterListKeys.isOptional]: yup
    .boolean()
    .required('Required status must be specified'),
})

const urlSchema = yup.string().url('Invalid URL format')

export const detectPathParameters = (url: string): string[] => {
  const pathParamRegex = /\{([^}]+)\}/g
  const matches = url.match(pathParamRegex)
  if (!matches) return []

  return matches.map((match) => match.slice(1, -1))
}

const validateUrlWithPathParameters = (
  url: string,
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = []

  try {
    const pathParams = detectPathParameters(url)
    const uniquePathParams = [...new Set(pathParams)]

    // Duplicate validation
    if (pathParams.length !== uniquePathParams.length) {
      const duplicates = pathParams.filter(
        (param, index) => pathParams.indexOf(param) !== index,
      )
      errors.push(
        `Path parameter '${duplicates[0]}' is used multiple times in the URL`,
      )
    }

    const validationErrors = uniquePathParams
      .map((param) => {
        try {
          actionParameterNameValidationSchema.validateSync(param)
          return ''
        } catch (error) {
          if (error instanceof yup.ValidationError) {
            return error.message
          }
          return 'Invalid path parameter name'
        }
      })
      .filter((error) => error !== '')

    if (validationErrors.length > 0) {
      errors.push(...validationErrors)
    }

    // Test url with example params
    const testUrl = uniquePathParams.reduce(
      (acc, param) => acc.replace(`{${param}}`, 'test123'),
      url,
    )

    try {
      urlSchema.validateSync(testUrl)
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        errors.push('Invalid URL structure')
      }
    }

    return { isValid: errors.length === 0, errors }
  } catch (error) {
    errors.push('Invalid URL structure')
    return { isValid: false, errors }
  }
}

const yupValidateUrlWithPathParameters = ({
  context,
  value,
}: {
  context: yup.TestContext<yup.AnyObject>
  value: string
}) => {
  const validation = validateUrlWithPathParameters(value)

  if (!validation.isValid) {
    return context.createError({
      message: validation.errors[0] || 'Invalid URL with path parameters',
    })
  }

  return true
}

export const scenarioActionValidationSchema = yup.object().shape(
  {
    [EScenarioActionKeys.parametersList]: yup
      .array(
        yup.object().shape({
          ...getParameterListSchema({ getParametersList: () => [] }),
          [EFormParameterListKeys.properties]: yup
            .array(
              yup.object().shape({
                ...getParameterListSchema({ getParametersList: () => [] }),
              }),
            )
            .when([EFormParameterListKeys.type], {
              is: (type: string) => type === 'object' || type === 'array',
              then: (schema) =>
                schema
                  .required('You must add at least one property')
                  .min(1, 'You must add at least one property'),
              otherwise: (schema) => schema.optional(),
            }),
        }),
      )
      .optional(),
    [EScenarioActionKeys.waitMessage]: yup
      .string()
      .max(191, 'Wait message is too long')
      .optional(),
    [EScenarioActionKeys.failureMessage]: yup
      .string()
      .required('Error message is required'),
    [EScenarioActionKeys.allPropertiesRequired]: yup.boolean().optional(),
    [EScenarioActionKeys.usesTemplate]: yup
      .mixed()
      .oneOf(Object.values(EScenarioActionUsesTemplate))
      .optional(),
    [EScenarioActionKeys.webhook]: yup
      .string()
      .url('Invalid URL format')
      .optional(),
    [EScenarioActionKeys.description]: yup
      .string()
      .max(191, 'Description is too long')
      .optional(),
    [EScenarioActionKeys.method]: yup
      .mixed()
      .oneOf(Object.values(EScenarioActionMethod))
      .optional(),
    [EScenarioActionKeys.authHeader]: yup
      .string()
      .when(EScenarioActionKeys.apiKey, {
        is: (apiKey: string) => !!apiKey,
        then: (schema) =>
          schema.required('Auth Header is required when API Key is provided'),
      }),
    [EScenarioActionKeys.apiKey]: yup.string().optional(),
    [EScenarioActionKeys.headers]: yup
      .array(
        yup.object().shape({
          key: yup
            .string()
            .matches(/^\S*$/, 'Key cannot contain spaces')
            .required('Key is required'),
          value: yup.string().required('Value is required'),
        }),
      )
      .test('unique-keys', 'Keys must be unique', (headers) => {
        if (!headers) {
          return true
        }

        const keys = headers.map((item) => item.key)
        return new Set(keys).size === keys.length
      })
      .optional(),
  },
  [[EScenarioActionKeys.authHeader, EScenarioActionKeys.apiKey]],
)

const createScenarioInstructionStepValidationSchema = yup.object().shape({
  description: yup.string().required('Description is required'),
})

export const createScenarioStepsFormValidationSchema = yup.object().shape({
  actionSteps: yup.array().of(
    yup.lazy((value) => {
      if (
        value.type === EActionStepType.API_CALL &&
        value.enableNewApiActions
      ) {
        return scenarioActionTitleValidationSchema
          .concat(scenarioActionValidationSchema)
          .concat(createScenarioInstructionStepValidationSchema)
      }

      return createScenarioInstructionStepValidationSchema
    }),
  ),
})

export const createScenarioCustomFieldsValidationSchema = yup.object({
  scenarioRules: yup.string(),
  agentCustomFields: yup.array().of(
    yup.object({
      key: yup.string().required('Key is required'),
      type: yup.string().required('Type is required'),
      description: yup.string(),
    }),
  ),
})

export const getScenarioActionTitlePayloadWithDefaults = (
  scenarioAction?: Partial<TScenarioActionPayload> | null,
): TUpdateScenarioTitlePayload => ({
  [EScenarioActionKeys.name]: scenarioAction?.[EScenarioActionKeys.name] || '',
})

export const getScenarioActionPayloadWithDefaults = (
  scenarioAction?: Partial<TScenarioActionPayload> | null,
): TFormScenarioActionPayload => ({
  [EScenarioActionKeys.name]: scenarioAction?.name || '',
  [EScenarioActionKeys.description]:
    scenarioAction?.[EScenarioActionKeys.description] || '',
  [EScenarioActionKeys.waitMessage]:
    scenarioAction?.[EScenarioActionKeys.waitMessage] || '',
  [EScenarioActionKeys.webhook]:
    scenarioAction?.[EScenarioActionKeys.webhook] || '',
  [EScenarioActionKeys.parametersList]: Object.entries(
    scenarioAction?.[EScenarioActionKeys.parametersList] || {},
  ).map(
    ([key, value]) =>
      ({
        [EFormParameterListKeys.id]: nanoid(),
        [EFormParameterListKeys.name]: key,
        [EFormParameterListKeys.description]:
          value[EParameterListKeys.description],
        [EFormParameterListKeys.isOptional]:
          value[EParameterListKeys.type].includes('null'),
        [EFormParameterListKeys.type]:
          typeof value[EParameterListKeys.type] === 'string'
            ? value[EParameterListKeys.type]
            : value[EParameterListKeys.type][0],
      }) as TFormParameterListContent,
  ),
  [EScenarioActionKeys.failureMessage]:
    scenarioAction?.[EScenarioActionKeys.failureMessage] || '',
  allPropertiesRequired:
    scenarioAction?.[EScenarioActionKeys.allPropertiesRequired] ?? true,
  usesTemplate:
    scenarioAction?.[EScenarioActionKeys.usesTemplate] ||
    EScenarioActionUsesTemplate.NO,
  [EScenarioActionKeys.method]:
    scenarioAction?.[EScenarioActionKeys.method] || undefined,
  [EScenarioActionKeys.authHeader]: '',
  [EScenarioActionKeys.apiKey]: '',
  [EScenarioActionKeys.headers]:
    Object.entries(
      JSON.parse(scenarioAction?.[EScenarioActionKeys.headers] || '{}'),
    ).reduce(
      (
        acc: { key: string; value: string }[],
        [key, value]: [string, unknown],
      ) => {
        acc.push({ key, value: value as string })
        return acc
      },
      [] as { key: string; value: string }[],
    ) || [],
})

export const getScenarioActionFormPayloadForApi = (
  values: TFormScenarioActionPayload,
): TScenarioActionPayload => ({
  ...values,
  [EScenarioActionKeys.headers]: JSON.stringify(
    (values[EScenarioActionKeys.headers] || []).reduce(
      (
        acc: { [x: string]: string },
        currentItem: { key: string; value: string },
      ) => {
        acc[currentItem.key] = currentItem.value
        return acc
      },
      {} as { [x: string]: string },
    ),
  ),
  [EScenarioActionKeys.parametersList]: (
    values[EScenarioActionKeys.parametersList] || []
  ).reduce(
    (acc, currentItem) => {
      acc[currentItem[EFormParameterListKeys.name]] = {
        [EParameterListKeys.description]:
          currentItem[EFormParameterListKeys.description],
        [EParameterListKeys.required]: true,
        [EParameterListKeys.type]: currentItem[
          EFormParameterListKeys.isOptional
        ]
          ? [currentItem[EFormParameterListKeys.type], 'null']
          : currentItem[EFormParameterListKeys.type],
      }

      return acc
    },
    {} as Record<string, TParameterListContent>,
  ),
})

export const getObjectOfPropertiesFromArrayOfProperties = (
  values: TFormParameterListContent[],
) => {
  return values.reduce(
    (acc, currentItem) => {
      const baseParameterType = currentItem[EFormParameterListKeys.type]
      const isParameterRequired =
        !currentItem[EFormParameterListKeys.isOptional]
      const resolvedParameterType: TParameterListType = isParameterRequired
        ? baseParameterType
        : [baseParameterType, 'null']

      acc[currentItem[EFormParameterListKeys.name]] = {
        [EParameterListKeys.description]:
          currentItem[EFormParameterListKeys.description],
        [EParameterListKeys.required]: true, // Send always true, BE use type to check optional parameter
        [EParameterListKeys.type]: resolvedParameterType,
        [EParameterListKeys.isPathParameter]:
          currentItem[EFormParameterListKeys.isPathParameter] ?? false,

        ...(currentItem[EFormParameterListKeys.properties] &&
          currentItem[EFormParameterListKeys.type] === 'object' && {
            [EParameterListKeys.properties]:
              getObjectOfPropertiesFromArrayOfProperties(
                currentItem[EFormParameterListKeys.properties] || [],
              ),
          }),

        ...(currentItem[EFormParameterListKeys.type] === 'array' && {
          [EParameterListKeys.properties]: currentItem[
            EFormParameterListKeys.properties
          ]?.map((property) => {
            return {
              [EParameterListKeys.description]:
                property[EFormParameterListKeys.description],
              [EParameterListKeys.required]:
                !property[EFormParameterListKeys.isOptional],
              [EParameterListKeys.type]: property[EFormParameterListKeys.type],
              [EParameterListKeys.name]: property[EFormParameterListKeys.name],
            }
          }),
        }),
      }
      return acc
    },
    {} as Record<string, TParameterListContent>,
  )
}

export const getScenarioActionFormPayloadForApiFromScenarioStep = (
  values: TActionApiCallStep | TScenarioNewAction,
) => {
  const newAction: TScenarioActionPayload = {
    [EScenarioActionKeys.name]: values.name || '',
    [EScenarioActionKeys.description]: values.apiActionDescription || '',
    [EScenarioActionKeys.waitMessage]: values.waitMessage || '',
    [EScenarioActionKeys.webhook]: values.webhook || '',
    [EScenarioActionKeys.parametersList]:
      getObjectOfPropertiesFromArrayOfProperties(values.parametersList || []),

    [EScenarioActionKeys.failureMessage]: values.failureMessage || '',
    [EScenarioActionKeys.allPropertiesRequired]: true,
    [EScenarioActionKeys.usesTemplate]: EScenarioActionUsesTemplate.NO,
    [EScenarioActionKeys.method]:
      values.httpMethod || EScenarioActionMethod.GET,
    [EScenarioActionKeys.connectionId]:
      'connectionId' in values ? values.connectionId : undefined,
    [EScenarioActionKeys.headers]: JSON.stringify(
      (values.headers ?? []).reduce(
        (acc, header) => {
          acc[header.key] = header.value
          return acc
        },
        {} as Record<string, string>,
      ),
    ),
    [EScenarioActionKeys.scenarioTemplateActionId]:
      EScenarioActionKeys.scenarioTemplateActionId in values
        ? values[EScenarioActionKeys.scenarioTemplateActionId]
        : undefined,
  }

  return newAction
}

function parseParameterProperties(
  propertiesObj: Record<string, TParameterListContent> | undefined,
): TFormParameterListContent[] {
  if (!propertiesObj) return []

  return Object.entries(propertiesObj).map(([name, param]) => {
    const base: TFormParameterListContent = {
      [EFormParameterListKeys.id]: nanoid(),
      [EFormParameterListKeys.name]: name,
      [EFormParameterListKeys.description]: param.description,
      [EFormParameterListKeys.isOptional]: !param.required,
      [EFormParameterListKeys.type]: Array.isArray(param.type)
        ? param.type[0]
        : param.type,
    }

    // Recursively parse nested properties for 'object'
    const { properties } = param as unknown as TFormParameterListContent
    if (base[EFormParameterListKeys.type] === 'object' && properties) {
      base[EFormParameterListKeys.properties] = parseParameterProperties(
        properties as unknown as Record<string, TParameterListContent>,
      )
    }

    // Recursively parse nested properties for 'array'
    if (
      base[EFormParameterListKeys.type] === 'array' &&
      Array.isArray(properties)
    ) {
      base[EFormParameterListKeys.properties] = (properties ?? []).map(
        (property) => {
          return {
            [EFormParameterListKeys.id]: nanoid(),
            [EFormParameterListKeys.name]: property.name,
            [EFormParameterListKeys.description]: property.description,
            [EFormParameterListKeys.isOptional]: !(
              property as unknown as TAgentCustomField
            ).required,
            [EFormParameterListKeys.type]: Array.isArray(property.type)
              ? property.type[0]
              : property.type,
          } as TFormParameterListContent
        },
      )
    }

    return base
  })
}

// TODO: Deprecated
export const getScenarioActionStepFromApiPayload = (
  payload: TScenarioActionProps,
  content: string,
): TActionApiCallStep => {
  // Convert headers JSON string to array of { key, value }
  const headers: { key: string; value: string; id: string }[] = payload.headers
    ? Object.entries(JSON.parse(payload.headers)).map(([key, value]) => ({
        id: nanoid(),
        key,
        value: value as string,
      }))
    : []

  // Convert parametersList object to array
  const parametersList: TFormParameterListContent[] = payload.parametersList
    ? parseParameterProperties(
        payload.parametersList as Record<string, TParameterListContent>,
      )
    : []

  return {
    name: payload?.name || '',
    webhook: payload?.webhook || '',
    httpMethod: payload.httpMethod,
    authHeader: '',
    apiKey: '',
    waitMessage: payload?.waitMessage || '',
    failureMessage: payload?.failureMessage || '',
    parametersList,
    headers,
    enableNewApiActions: true,
    apiActionDescription: payload?.description || '',
    id: payload?.id || '',
    type: EActionStepType.API_CALL,
    description: content,
  }
}

export const getInstructionStepsScenarioActionSteps = (
  actionSteps: TActionStep[],
) => {
  return actionSteps.reduce(
    (acc, step, index) => {
      acc[index + 1] = step?.description || ''
      return acc
    },

    {} as Record<number, string>,
  )
}

export const getActionIdsFromInstructions = (
  instructions: string,
  actions: TScenarioAction[],
) => {
  const regex = /\{\{(.*?)\}\}/g
  const matches = instructions.match(regex)

  return (matches || []).reduce((acc, currentMatch) => {
    const correspondingId = actions.find(
      (action) => `{{${action[EScenarioActionKeys.name]}}}` === currentMatch,
    )?.[EScenarioActionKeys.id]

    if (correspondingId && !acc.includes(correspondingId)) {
      acc.push(correspondingId)
    }

    return acc
  }, [] as string[])
}

export const getPartsFromAction = (line: string) =>
  line.split(/(\{\{[^}]+\}\})/).filter(Boolean)

export const getInstructionStepsFromForm = (
  formInstructionSteps: string,
): Record<number, string> => {
  const steps = formInstructionSteps
    .split('\n')
    .map((step) => step.replace(/^\d+\.\s*/, ''))
    .filter((step) => step.trim() !== '')
    .reduce(
      (acc, step, index) => {
        acc[index + 1] = step
        return acc
      },
      {} as Record<number, string>,
    )
  return steps
}

export const getFormInstructionsStepsFromScenario = (
  instructionSteps: TInstructionStep[] | undefined | null,
): string => {
  return (instructionSteps || [])
    .sort((a, b) => {
      return a.stepNumber - b.stepNumber
    })
    .map((step) => `${step.stepNumber}. ${step.content}\n`)
    .join('')
}

export const getScenarioTitlePayloadWithDefaults = (
  scenario?: Partial<TScenarioPayload> | null,
): TUpdateScenarioTitlePayload => ({
  [EScenarioKeys.name]: scenario?.[EScenarioKeys.name] || DEFAULT_SCENARIO_NAME,
})

export const getScenarioPayloadWithDefaults = (
  scenario?: Partial<TScenario> | null,
): TUpdateScenarioPayload => ({
  ...scenario,
  [EScenarioKeys.instructionSteps]: getFormInstructionsStepsFromScenario(
    scenario?.[EScenarioKeys.instructionStep],
  ),
  [EScenarioKeys.additionalInstructions]:
    scenario?.[EScenarioKeys.additionalInstructions] || '',
  [EScenarioKeys.triggerInstructions]:
    scenario?.[EScenarioKeys.triggerInstructions] || '',
  [EScenarioKeys.agentCustomFields]:
    Object.entries(scenario?.[EScenarioKeys.agentCustomFields] || {}).map(
      ([key, value]) => ({
        [EFormAgentCustomFieldKeys.key]: key,
        ...value,
      }),
    ) || [],
  [EScenarioKeys.channels]: scenario?.[EScenarioKeys.channels] || [],
  [EScenarioKeys.actionIds]: scenario?.[EScenarioKeys.actionIds] || [],
})

export const getNumberedInstructions = (
  instructions: string,
  isEmptyLineAllowed: boolean = false,
) => {
  return instructions
    .split('\n')
    .filter((line) => !/^\d+\.$/.test(line)) // Remove lines that are just a number and a dot
    .filter(
      (line) => isEmptyLineAllowed || line.replace(/^\d+\.\s*/, '') !== '',
    ) // Remove empty lines if not allowed
    .filter(
      (line, index, arr) =>
        !(
          line.replace(/^\d+\.\s*/, '') === '' &&
          arr[index - 1]?.replace(/^\d+\.\s*/, '') === ''
        ),
    )
    .map((line, index) => {
      const cleanLine = line.replace(/^\d+\.\s*/, '') // Remove existing numbers from the start of the line
      return `${index + 1}. ${cleanLine}`
    })
    .join('\n')
}

type FastFieldProps<TMetaProps = { extraFields?: string[] }, TForm = object> = {
  formik: FormikProps<TForm>
  name: string
  meta?: TMetaProps
}

const getIsMetaPropsChanged = (
  nextMetaProps?: Record<string, unknown>,
  currentMetaProps?: Record<string, unknown>,
) => {
  const nextMetaPropsValues = nextMetaProps ? Object.values(nextMetaProps) : []
  const currentMetaPropsValues = currentMetaProps
    ? Object.values(currentMetaProps)
    : []

  return nextMetaPropsValues.some(
    (value, index) => value !== currentMetaPropsValues[index],
  )
}

export const shouldRerenderFastField = (
  nextProps: FastFieldProps,
  currentProps: FastFieldProps,
) => {
  const extraFields = nextProps.meta?.extraFields

  const isExtraFieldsChanged =
    extraFields?.some((field) => {
      return (
        getIn(nextProps.formik.values, field) !==
        getIn(currentProps.formik.values, field)
      )
    }) || false

  return (
    nextProps.name !== currentProps.name ||
    nextProps.formik.isSubmitting !== currentProps.formik.isSubmitting ||
    Object.keys(nextProps).length !== Object.keys(currentProps).length ||
    getIn(nextProps.formik.values, currentProps.name) !==
      getIn(currentProps.formik.values, currentProps.name) ||
    getIn(nextProps.formik.errors, currentProps.name) !==
      getIn(currentProps.formik.errors, currentProps.name) ||
    getIn(nextProps.formik.touched, currentProps.name) !==
      getIn(currentProps.formik.touched, currentProps.name) ||
    getIsMetaPropsChanged(nextProps.meta, currentProps.meta) ||
    isExtraFieldsChanged
  )
}

export const actionParameterListToFormParameterList = (
  actionParamaterList?: Record<string, TParameterListContent>,
) => {
  const parameterList: TFormParameterListContent[] = []

  if (actionParamaterList) {
    Object.entries(actionParamaterList).forEach(
      ([parameterName, parameterContent]) => {
        const newFormParamaterListContent: TFormParameterListContent = {
          [EFormParameterListKeys.id]: nanoid(),
          [EFormParameterListKeys.name]: parameterName,
          [EFormParameterListKeys.description]:
            parameterContent[EParameterListKeys.description],
          [EFormParameterListKeys.isOptional]:
            typeof parameterContent[EParameterListKeys.type] !== 'string',
          // Always we send 'required: true' to the BE, so, if we get an Array in the type
          // The parameter is optional, if not, the parameter is required
          [EFormParameterListKeys.type]:
            typeof parameterContent[EParameterListKeys.type] === 'string'
              ? parameterContent[EParameterListKeys.type]
              : parameterContent[EParameterListKeys.type][0],
          [EFormParameterListKeys.isPathParameter]:
            parameterContent[EParameterListKeys.isPathParameter] ?? false,
        }

        if (
          EFormParameterListKeys.properties in parameterContent &&
          !(
            parameterContent[EFormParameterListKeys.properties] instanceof Array
          )
        ) {
          newFormParamaterListContent[EFormParameterListKeys.properties] =
            actionParameterListToFormParameterList(
              parameterContent[EParameterListKeys.properties],
            )
        }

        parameterList.push(newFormParamaterListContent)
      },
    )
  }

  return parameterList
}

export const actionHeadersToFormHeaderList = (headers?: string | null) => {
  const headersListItems: TFormHeaderListContent[] = []

  if (headers) {
    try {
      Object.entries(JSON.parse(headers) as Record<string, string>).forEach(
        ([key, value]) => {
          const newFormHeaderListContent: TFormHeaderListContent = {
            id: nanoid(),
            key,
            value,
          }

          headersListItems.push(newFormHeaderListContent)
        },
      )
    } catch {
      //
    }
  }

  return headersListItems
}

const ACTION_SCHEMA_ACTION_FORM_KEYS = {
  [EActionKeys.name]: EActionKeys.name,
  [EActionKeys.description]: EActionKeys.description,
  [EActionKeys.httpMethod]: EActionKeys.httpMethod,
  [EActionKeys.webhook]: EActionKeys.webhook,
  [EActionKeys.headers]: EActionKeys.headers,
  [EActionKeys.parametersList]: EActionKeys.parametersList,
  [EActionKeys.failureMessage]: EActionKeys.failureMessage,
  [EActionKeys.connectionId]: EActionKeys.connectionId,
}

const ACTION_SCHEMA_SCENARIO_FORM_KEYS = {
  [EActionKeys.name]: EScenarioActionKeys.name,
  [EActionKeys.description]: EScenarioActionKeys.description,
  [EActionKeys.httpMethod]: EScenarioActionKeys.method,
  [EActionKeys.webhook]: EScenarioActionKeys.webhook,
  [EActionKeys.headers]: EScenarioActionKeys.headers,
  [EActionKeys.parametersList]: EScenarioActionKeys.parametersList,
  [EActionKeys.failureMessage]: EScenarioActionKeys.failureMessage,
  [EActionKeys.connectionId]: EScenarioActionKeys.connectionId,
}

const ACTION_SCHEMA_KEYS_MAP = {
  'action-form': ACTION_SCHEMA_ACTION_FORM_KEYS,
  'scenario-form': ACTION_SCHEMA_SCENARIO_FORM_KEYS,
}

/**
 * Common validation schema for create/edit action pages and create action form in create/edit scenario.
 * This schema is shared between the standalone action form and the action form within scenario creation.
 * When making changes to this schema, consider testing in both sections to ensure compatibility.
 */
export const getCommonCreateEditActionSchema = ({
  actions,
  getActionNames,
  getHeaders,
  getParametersList,
  getParametersListFromProperties,
  mode,
}: {
  actions: TAction[]
  getActionNames: (
    actions: TAction[],
    context: yup.TestContext<yup.AnyObject>,
  ) => Set<string>
  getHeaders: (
    context: yup.TestContext<yup.AnyObject>,
  ) => TFormHeaderListContent[]
  getParametersList: (
    context: yup.TestContext<yup.AnyObject>,
  ) => TFormParameterListContent[]
  getParametersListFromProperties: (
    context: yup.TestContext<yup.AnyObject>,
  ) => TFormParameterListContent[]
  mode: 'action-form' | 'scenario-form'
}) => {
  const keys = ACTION_SCHEMA_KEYS_MAP[mode]

  return yup.object().shape({
    [keys[EActionKeys.name]]: actionNameBaseValidationSchema.test(
      'unique-keys',
      'Name must be unique',
      (name, context) => {
        if (!name) return true

        const actionNames = getActionNames(actions, context)

        return !actionNames.has(name)
      },
    ),
    [keys[EActionKeys.description]]: yup
      .string()
      .max(191, 'Description is too long')
      .optional(),
    [keys[EActionKeys.httpMethod]]: yup
      .mixed()
      .oneOf(Object.values(EScenarioActionMethod))
      .required(),
    [keys[EActionKeys.webhook]]: yup
      .string()
      .required('Endpoint URL is required')
      .test('url-format', 'Invalid URL format', (value, context) => {
        if (!value) return true

        try {
          urlSchema.validateSync(value)
          return true
        } catch {
          return yupValidateUrlWithPathParameters({ context, value })
        }
      })
      .test(
        'path-parameters',
        'Path parameter validation failed',
        (value, context) => {
          if (!value) return true

          return yupValidateUrlWithPathParameters({ context, value })
        },
      ),
    [keys[EActionKeys.headers]]: yup
      .array(
        yup.object().shape({
          key: yup
            .string()
            .matches(/^\S*$/, 'Key cannot contain spaces')
            .required('Key is required')
            .test('unique-keys', 'Keys must be unique', (key, context) => {
              const headers = getHeaders(context)

              const headersKeys = headers.map((field) => field.key)
              const headerCounts = headersKeys.reduce<Record<string, number>>(
                (acc, headerKey) => {
                  acc[headerKey] = (acc[headerKey] || 0) + 1
                  return acc
                },
                {},
              )

              const duplicateKeys = Object.entries(headerCounts)
                .filter(([, count]) => count > 1)
                .map(([headerKey]) => headerKey)

              const isValid = !duplicateKeys.includes(key)

              return isValid
            }),
          value: yup.string().required('Value is required'),
        }),
      )
      .optional(),
    [keys[EActionKeys.parametersList]]: yup
      .array(
        yup.object().shape({
          ...getParameterListSchema({ getParametersList }),
          [EFormParameterListKeys.properties]: yup
            .array(
              yup.object().shape({
                ...getParameterListSchema({
                  getParametersList: getParametersListFromProperties,
                }),
              }),
            )
            .when([EFormParameterListKeys.type], {
              is: (type: string) => type === 'object' || type === 'array',
              then: (schema) =>
                schema
                  .required('You must add at least one property')
                  .min(1, 'You must add at least one property'),
              otherwise: (schema) => schema.optional(),
            }),
        }),
      )
      .optional(),
    [keys[EActionKeys.failureMessage]]: yup
      .string()
      .required('Error message is required'),
    [keys[EActionKeys.connectionId]]: yup
      .string()
      .nullable()
      .required('Connection is required'),
  })
}
